import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import HeroSection from "@/components/HeroSection";
import { useIsMobile } from "@/hooks/use-mobile";
import Reveal from "@/components/Reveal";
import { ArrowRight } from "lucide-react";

// Defer below-the-fold sections so they don't compete with the hero for main-thread time
const ProductShowcase = lazy(() => import("@/components/ProductShowcase"));
const PortfolioShowcase = lazy(() => import("@/components/PortfolioShowcase"));
const TrustedByCarousel = lazy(() => import("@/components/TrustedByCarousel"));
const FooterAnimation = lazy(() => import("@/components/FooterAnimation"));

const Index = () => {
  const isMobile = useIsMobile();
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const tryScroll = (attempt = 0) => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (attempt < 20) {
        setTimeout(() => tryScroll(attempt + 1), 100);
      }
    };
    tryScroll();
  }, [location.hash]);

  return (
    <main className="bg-background">
      <HeroSection />

      {/* Section 1 - Product Showcase */}
      <section
        id="services"
        className="w-full bg-white flex items-center justify-center"
        style={{
          minHeight: isMobile ? undefined : "100vh",
          padding: isMobile ? "64px 16px" : "80px 32px",
        }}
      >
        <Reveal
          className={
            isMobile
              ? "flex flex-col items-start w-full"
              : "flex items-center gap-12 lg:gap-16 max-w-[1400px] w-full"
          }
        >
          {isMobile ? (
            <div className="flex flex-col items-start w-full mb-6">
              <h2
                className="font-poppins font-semibold leading-tight"
                style={{ fontSize: "clamp(28px, 8vw, 36px)", color: "hsl(0, 0%, 8%)" }}
              >
                These are
                <br />
                our services
              </h2>
              <ArrowRight
                style={{ width: 36, height: 36, color: "hsl(0, 0%, 8%)", marginTop: 8 }}
                strokeWidth={1.5}
              />
            </div>
          ) : (
            <div className="hidden md:flex flex-col items-start shrink-0">
              <h2
                className="font-poppins font-semibold leading-tight whitespace-nowrap"
                style={{ fontSize: "clamp(32px, 3.5vw, 48px)", color: "hsl(0, 0%, 8%)" }}
              >
                These are
                <br />
                our services
              </h2>
              <div className="w-full flex justify-end mt-3">
                <ArrowRight
                  style={{ width: 56, height: 56, color: "hsl(0, 0%, 8%)" }}
                  strokeWidth={1.5}
                />
              </div>
            </div>
          )}

          {isMobile ? (
            <MobileScaledShowcase />
          ) : (
            <div className="flex-1 min-w-0">
              <Suspense fallback={null}>
                <ProductShowcase />
              </Suspense>
            </div>
          )}
        </Reveal>
      </section>

      <Suspense fallback={null}>
        <PortfolioShowcase />
      </Suspense>
      <Reveal>
        <Suspense fallback={null}>
          <TrustedByCarousel />
        </Suspense>
      </Reveal>
      <Suspense fallback={null}>
        <FooterAnimation />
      </Suspense>
    </main>
  );
};

/**
 * Renders the desktop ProductShowcase at a fixed virtual width and scales it
 * to fit its parent container width, preserving pixel-perfect positioning.
 */
const MobileScaledShowcase = () => {
  const VIRTUAL_WIDTH = 560; // px
  const VIRTUAL_HEIGHT = (VIRTUAL_WIDTH * 10) / 16;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / VIRTUAL_WIDTH);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("orientationchange", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="w-full"
      style={{
        aspectRatio: `${VIRTUAL_WIDTH} / ${VIRTUAL_HEIGHT}`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: VIRTUAL_WIDTH,
          height: VIRTUAL_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        <Suspense fallback={null}>
          <ProductShowcase />
        </Suspense>
      </div>
    </div>
  );
};

export default Index;
